/**
 * Scratch arrays kept from one solve to the next (1.K-2 deliverable 15, performance). A solve asks
 * for its typed arrays in much the same order every time; the n-th request of a solve gets the
 * n-th array of the shelf (grown when too short), cleared to the value asked for, exactly as a new
 * array would be. Nothing taken here outlives the solve that took it (no result holds one), and
 * `resetScratch` starts the next solve from the first array again. JavaScript runs one solve at a
 * time: no user code runs between the reset and the end of a solve.
 */
class Shelf {
    make;
    items = [];
    next = 0;
    constructor(make) {
        this.make = make;
    }
    /** An array of `length`, set to `fill`; left as it was when `fill` is null (every slot is written before it is read). */
    take(length, fill) {
        let item = this.items[this.next];
        if (item === undefined || item.length < length) {
            item = this.make(Math.max(length, 16));
            this.items[this.next] = item;
        }
        this.next++;
        const view = item.subarray(0, length);
        if (fill !== null)
            view.fill(fill);
        return view;
    }
    reset() {
        this.next = 0;
    }
    position() {
        return this.next;
    }
    rewind(to) {
        this.next = to;
    }
    held() {
        let bytes = 0;
        for (const item of this.items)
            bytes += item.byteLength;
        return { items: this.items.length, bytes };
    }
}
const INT32 = new Shelf((n) => new Int32Array(n));
const FLOAT64 = new Shelf((n) => new Float64Array(n));
const UINT8 = new Shelf((n) => new Uint8Array(n));
/** An Int32Array of `length` set to `fill`, from the shelf. */
export const int32 = (length, fill = 0) => INT32.take(length, fill);
/** A Float64Array of `length` set to `fill`, from the shelf. */
export const float64 = (length, fill = 0) => FLOAT64.take(length, fill);
/** A Uint8Array of `length` set to `fill`, from the shelf. */
export const uint8 = (length, fill = 0) => UINT8.take(length, fill);
/** An Int32Array of `length` whose every slot the caller writes before reading it. */
export const int32Unset = (length) => INT32.take(length, null);
/** A copy of `source` on a shelf array. */
export function copyInt32(source) {
    const copy = INT32.take(source.length, null);
    copy.set(source);
    return copy;
}
export function copyFloat64(source) {
    const copy = FLOAT64.take(source.length, null);
    copy.set(source);
    return copy;
}
export function copyUint8(source) {
    const copy = UINT8.take(source.length, null);
    copy.set(source);
    return copy;
}
/** Starts a solve: every shelf array may be taken again. */
export function resetScratch() {
    INT32.reset();
    FLOAT64.reset();
    UINT8.reset();
}
export function scratchMark() {
    return [INT32.position(), FLOAT64.position(), UINT8.position()];
}
/** Gives back every array taken since `mark`; the caller holds none of them any more. */
export function scratchRelease(mark) {
    INT32.rewind(mark[0]);
    FLOAT64.rewind(mark[1]);
    UINT8.rewind(mark[2]);
}
/** What the shelves hold now (arrays and bytes), for the specs: a solve must not leave more than its size needs. */
export function scratchStats() {
    const parts = [INT32.held(), FLOAT64.held(), UINT8.held()];
    return {
        items: parts.reduce((t, x) => t + x.items, 0),
        bytes: parts.reduce((t, x) => t + x.bytes, 0),
    };
}
/** The numbers of `list` on a shelf Int32Array. */
export function int32From(list) {
    const copy = INT32.take(list.length, null);
    for (let i = 0; i < list.length; i++)
        copy[i] = list[i];
    return copy;
}
//# sourceMappingURL=pool.js.map